'use client'

function formatTime(dateString) {
  if (!dateString) return '--:--'
  const d = new Date(dateString)

  if (isNaN(d.getTime())) return '--:--'

  return d.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Madrid'
  })
}

function getStatusLabel(status) {
  const labels = {
    live: 'EN JOC',
    scheduled: 'PROGRAMAT',
    finished: 'FINALITZAT',
    paused: 'PAUSAT'
  }
  return labels[status] || status?.toUpperCase() || 'PROGRAMAT'
}

function formatGamePoints(points) {
  const mapping = { 0: '0', 1: '15', 2: '30', 3: '40' }
  return mapping[points] ?? points
}

function getCurrentGameScore(currentState) {
  if (!currentState) return { team1: '0', team2: '0' }

  const gamePoints = currentState.game_points || [0, 0]
  const currentSet = currentState.sets?.[currentState.current_set_idx]

  if (currentSet?.tiebreak) {
    return {
      team1: String(currentSet.team1_tiebreak_points || 0),
      team2: String(currentSet.team2_tiebreak_points || 0)
    }
  }

  return {
    team1: formatGamePoints(gamePoints[0]),
    team2: formatGamePoints(gamePoints[1])
  }
}

function getPlayedSetsScores(currentState) {
  if (!currentState?.sets) {
    return []
  }

  const playedSets = []

  for (let i = 0; i < currentState.sets.length; i++) {
    const set = currentState.sets[i]

    if (set.finished) {
      playedSets.push({
        team1: set.team1 ?? 0,
        team2: set.team2 ?? 0,
        finished: true,
        current: false
      })
    } else if (i === currentState.current_set_idx) {
      playedSets.push({
        team1: set.team1 ?? 0,
        team2: set.team2 ?? 0,
        finished: false,
        current: true
      })

      break
    }
  }

  return playedSets
}

function formatTeamName(player1, player2) {
  const p1 = player1?.trim() || ''
  const p2 = player2?.trim() || ''

  if (!p1 && !p2) return '—'
  if (!p2) return p1
  if (!p1) return p2

  return `${p1} / ${p2}`
}

function getLastName(fullName) {
  if (!fullName) return ''
  const parts = fullName.trim().split(' ')
  return parts[parts.length - 1]
}

function formatTeamNameMobile(player1, player2) {
  const p1 = getLastName(player1)
  const p2 = getLastName(player2)

  if (!p1 && !p2) return '—'
  if (!p2) return p1
  if (!p1) return p2

  return `${p1} / ${p2}`
}

export function Scoreboard({ match, basePath }) {
  const {
    id,
    team1_player1,
    team1_player2,
    team2_player1,
    team2_player2,
    tournament_name,
    date,
    status,
    current_state,
    config,
    team1_badge_url,
    team2_badge_url
  } = match

  const isLive = status === 'live'
  const isFinished = status === 'finished'
  // const isScheduled = status === 'scheduled' || !status

  const playedSets = getPlayedSetsScores(current_state)
  const gameScore = getCurrentGameScore(current_state)

  const servingTeam = current_state?.serving_team

  const team1FullName = formatTeamName(team1_player1, team1_player2)
  const team1MobileName = formatTeamNameMobile(team1_player1, team1_player2)

  const team2FullName = formatTeamName(team2_player1, team2_player2)
  const team2MobileName = formatTeamNameMobile(team2_player1, team2_player2)

  return (
    <div
      className="border flex flex-col text-card-foreground bg-card border-white/5 overflow-hidden shadow-lg bg-[#082845] text-white"
      data-testid={`card-match-${id}`}
    >
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-start gap-2 min-h-[2.5rem]">
          <div className="flex-1 min-w-0 flex items-center">
            <span
              className="text-white/50 uppercase tracking-wider leading-tight break-words"
              style={{
                fontSize: '0.8rem',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {tournament_name || '—'}
            </span>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {!isLive && (
              <div className="flex items-center gap-1 text-xs text-white/50 mr-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-3 h-3"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {formatTime(date)}
              </div>
            )}

            <div
              className={`whitespace-nowrap tracking-wider inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold border-transparent rounded-md shadow-xs ${
                isLive
                  ? 'bg-red-500 text-blue animate-pulse'
                  : isFinished
                  ? 'bg-white/20 text-white'
                  : 'bg-white/10 text-white'
              }`}
            >
              {getStatusLabel(status)}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {team1_badge_url && (
                <img
                  src={`${basePath}/${team1_badge_url}`}
                  alt="Badge Team 1"
                  className="w-8 h-8 object-contain flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              )}

              <div className="hidden md:block font-display font-bold text-lg truncate uppercase text-white">
                {team1FullName}
              </div>
              <div className="block md:hidden font-display font-bold text-base truncate uppercase text-white">
                {team1MobileName}
              </div>

              {isLive && servingTeam === 1 && (
                <div className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" />
              )}
            </div>

            <div className="flex items-center gap-2 ml-4">
              {playedSets.length === 0 ? (
                <>
                  <span className="font-bold text-base w-6 text-center text-white">
                    0
                  </span>
                  <span className="font-bold text-base w-8 text-center bg-white/5 rounded px-1 text-white">
                    0
                  </span>
                </>
              ) : (
                <>
                  {playedSets.map((set, idx) => {
                    const lostSet = set.finished && set.team1 < set.team2

                    return (
                      <span
                        key={idx}
                        className={`font-bold text-base w-6 text-center ${
                          lostSet ? 'text-white/60' : 'text-white'
                        }`}
                      >
                        {set.team1}
                      </span>
                    )
                  })}
                  {!isFinished && (
                    <span className="font-bold text-base w-8 text-center bg-white/5 rounded px-1 text-white">
                      {gameScore.team1}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {team2_badge_url && (
                <img
                  src={`${basePath}/${team2_badge_url}`}
                  alt="Badge Team 2"
                  className="w-8 h-8 object-contain flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              )}

              <div className="hidden md:block font-display font-bold text-lg truncate uppercase text-white">
                {team2FullName}
              </div>
              <div className="block md:hidden font-display font-bold text-base truncate uppercase text-white">
                {team2MobileName}
              </div>

              {isLive && servingTeam === 2 && (
                <div className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" />
              )}
            </div>

            <div className="flex items-center gap-2 ml-4">
              {playedSets.length === 0 ? (
                <>
                  <span className="font-bold text-base w-6 text-center text-white">
                    0
                  </span>
                  <span className="font-bold text-base w-8 text-center bg-white/5 rounded px-1 text-white">
                    0
                  </span>
                </>
              ) : (
                <>
                  {playedSets.map((set, idx) => {
                    const lostSet = set.finished && set.team2 < set.team1

                    return (
                      <span
                        key={idx}
                        className={`font-bold text-base w-6 text-center ${
                          lostSet ? 'text-white/60' : 'text-white'
                        }`}
                      >
                        {set.team2}
                      </span>
                    )
                  })}
                  {!isFinished && (
                    <span className="font-bold text-base w-8 text-center bg-white/5 rounded px-1 text-white">
                      {gameScore.team2}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-black/20 p-4 flex justify-center mt-auto gap-8">
        <a
          href={`${basePath}/match/${id}/view`}
          target="_blank"
          rel="noreferrer"
        >
          <button
            className="inline-flex items-center justify-center whitespace-nowrap rounded-xl font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-transparent min-h-9 text-xs hover:bg-white/5 hover:text-yellow-500 hover:text-secondary p-2 text-white"
            data-testid={`button-view-${id}`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-4 h-4 mr-2"
            >
              <path d="M10 7.75a.75.75 0 0 1 1.142-.638l3.664 2.249a.75.75 0 0 1 0 1.278l-3.664 2.25a.75.75 0 0 1-1.142-.64z" />
              <path d="M12 17v4" />
              <path d="M8 21h8" />
              <rect x="2" y="3" width="20" height="14" rx="2" />
            </svg>
            Veure Marcador
          </button>
        </a>
        <a
          href={`${basePath}/match/${id}/estadistiques`}
          target="_blank"
          rel="noreferrer"
        >
          <button
            className="inline-flex items-center justify-center whitespace-nowrap rounded-xl font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-transparent min-h-9 text-xs hover:bg-white/5 hover:text-yellow-500 p-2 text-white"
            data-testid={`button-stats-${id}`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-4 h-4 mr-2"
            >
              <path d="M3 3v16a2 2 0 0 0 2 2h16" />
              <path d="M18 17V9" />
              <path d="M13 17V5" />
              <path d="M8 17v-3" />
            </svg>
            Veure Estadístiques
          </button>
        </a>
      </div>
    </div>
  )
}

export function ScoreboardGrid({ scoreboard = [], basePath }) {
  if (!scoreboard.length) return null

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {scoreboard.map((match) => (
        <Scoreboard key={match.id} match={match} basePath={basePath} />
      ))}
    </div>
  )
}

export function ScoreboardList({ scoreboard = [], basePath }) {
  if (!scoreboard.length) return null

  return (
    <div className="space-y-4">
      {scoreboard.map((match) => (
        <Scoreboard key={match.id} match={match} basePath={basePath} />
      ))}
    </div>
  )
}

export default Scoreboard
